/**
 * ENUM columns in the DB Manager (07.10.2026): which columns are enums, what
 * values they allow, and the list the cell editor shows.
 *
 * The values come from the table tab's existing column-metadata query
 * (DbManagerResultsTab.fetchPrimaryKey), not from a request per cell, and are
 * cached with the rest of that metadata per tab:
 *  - Postgres: the labels ride along as `enum_values` (pg_enum, ordered by
 *    enumsortorder — the declared order, ADD VALUE … BEFORE included). Enum
 *    ARRAY columns get none and stay text.
 *  - MySQL/MariaDB: SHOW COLUMNS already returns the `Type` text,
 *    `enum('a','b')`, parsed here. SET is multi-valued and is not an enum.
 *  - SQLite has no enum type.
 */

import { isBytesTag } from '../dbCellTags';

/**
 * A metadata cell as text. MySQL 8 sends SHOW COLUMNS' `Type` as a BLOB, so it
 * arrives as a bytes tag (base64 of UTF-8) rather than a string — pinned by
 * db_mysql_enum_live_tests.rs. MariaDB and the demo bridge send text.
 */
export function columnTypeText(v) {
  if (typeof v === 'string') return v;
  if (!isBytesTag(v)) return v == null ? '' : String(v);
  try {
    const bin = atob(v.b64);
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
    if (typeof TextDecoder !== 'undefined') return new TextDecoder('utf-8').decode(bytes);
    return decodeURIComponent(escape(bin));
  } catch {
    return '';
  }
}

/**
 * MySQL enum values for the editor, or null to keep the column on the text
 * editor. MySQL's column metadata is utf8mb3: a label with a 4-byte character
 * (an emoji) is printed with '?' in its place, so a list containing '?' may not
 * be the real one — picking from it would write a value the server refuses.
 */
export function mysqlEnumValuesForEditor(type) {
  const values = parseMysqlEnumType(columnTypeText(type));
  if (!values || values.some((v) => v.includes('?'))) return null;
  return values;
}

/** The NULL entry at the top of a nullable column's list. */
export const NULL_ITEM = Object.freeze({ null: true });

const BACKSLASH_ESCAPES = Object.freeze({ 0: '\0', n: '\n', r: '\r' });

/**
 * The values of a MySQL `enum(...)` column type, in declared order, or null
 * when the type is not an enum.
 *
 * The server prints each value as a quoted string literal: a quote inside is
 * doubled (`'it''s'`) and a backslash is doubled (`'a\\b'`) — pinned against a
 * real MySQL 8 by db_mysql_enum_live_tests.rs. Commas and parentheses inside
 * quotes are part of the value, so this walks the literal rather than
 * splitting on commas.
 */
export function parseMysqlEnumType(type) {
  if (typeof type !== 'string') return null;
  const m = /^\s*enum\s*\(([\s\S]*)\)\s*$/i.exec(type);
  if (!m) return null;
  const body = m[1];
  const values = [];
  let i = 0;
  const n = body.length;
  const skipSpace = () => {
    while (i < n && /\s/.test(body[i])) i += 1;
  };
  skipSpace();
  if (i >= n) return null;
  while (i < n) {
    if (body[i] !== "'") return null;
    i += 1;
    let v = '';
    let closed = false;
    while (i < n) {
      const ch = body[i];
      if (ch === "'") {
        if (body[i + 1] === "'") {
          v += "'";
          i += 2;
          continue;
        }
        i += 1;
        closed = true;
        break;
      }
      if (ch === '\\' && i + 1 < n) {
        // The server's append_unescaped writes NUL, LF and CR as \0 \n \r;
        // any other escaped character stands for itself.
        v += BACKSLASH_ESCAPES[body[i + 1]] ?? body[i + 1];
        i += 2;
        continue;
      }
      v += ch;
      i += 1;
    }
    if (!closed) return null;
    values.push(v);
    skipSpace();
    if (i >= n) break;
    if (body[i] !== ',') return null;
    i += 1;
    skipSpace();
  }
  return values.length ? values : null;
}

/** Postgres `enum_values` cell → string[] | null. */
export function pgEnumValues(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((v) => String(v));
}

/**
 * Column name → { values, nullable } for every enum column of a table tab's
 * metadata, or an empty map. Only table tabs have metadata, so a query tab —
 * whose columns are an arbitrary projection — never gets an enum editor.
 */
export function enumMetaByColumn(tableColumns) {
  const map = new Map();
  if (!Array.isArray(tableColumns)) return map;
  for (const c of tableColumns) {
    if (c && Array.isArray(c.enumValues) && c.enumValues.length > 0) {
      map.set(c.name, { values: c.enumValues, nullable: c.nullable !== false });
    }
  }
  return map;
}

/** The editor's list: NULL first when the column allows it, then the values. */
export function enumEditorItems(values, nullable) {
  const items = (values || []).map((value) => ({ value }));
  return nullable ? [NULL_ITEM, ...items] : items;
}

/**
 * Case-insensitive "contains" filter, values that START with the text first
 * (typing `p` puts `paid` above `shipped`). NULL matches the text "null".
 */
export function filterEnumItems(items, text) {
  const q = String(text || '').trim().toLowerCase();
  if (!q) return items;
  const label = (it) => (it === NULL_ITEM ? 'null' : it.value.toLowerCase());
  const starts = [];
  const contains = [];
  for (const it of items) {
    const l = label(it);
    if (l.startsWith(q)) starts.push(it);
    else if (l.includes(q)) contains.push(it);
  }
  return [...starts, ...contains];
}
